import ExpandIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Button, Grid, Typography } from '@mui/material';
import LabelledDropdown from 'components/atoms/Dropdown/LabelledDropdown';
import { DeconvolutionOperation, Study } from 'interfaces/study';
import { concat, first, map, size, split } from 'lodash';
import React, { useMemo } from 'react';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import { deconvolutionTypeToDefaultCoefficients } from '../helpers';
import { DeconvolutionOperationComponent } from './DeconvolutionOperationComponent';

export const DeconvolutionStainMapping: React.FC<{
  stainTypeId: string;
  updatedSettings: Study['settings'];
  setUpdatedSettings: React.Dispatch<React.SetStateAction<Study['settings']>>;
}> = ({ stainTypeId, updatedSettings, setUpdatedSettings }) => {
  const stainComponentsIds = useMemo(() => split(stainTypeId, '_'), [stainTypeId]);
  const [newComponentOperationType, setNewComponentOperationType] =
    React.useState<DeconvolutionOperation['deconvolutionType']>('cubic');
  const [newStainComponent, setNewStainComponent] = React.useState<string>(first(stainComponentsIds));

  const { stainTypeIdToDisplayName } = useStainTypeIdToDisplayName();

  const componentOperations = useMemo(
    () => updatedSettings.deconvolutionSettings?.stainToComponentOperation[stainTypeId] || [],
    [updatedSettings, stainTypeId]
  );

  return (
    <Accordion>
      <AccordionSummary expandIcon={<ExpandIcon />}>
        <Typography variant="h6">{stainTypeIdToDisplayName(stainTypeId)}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container>
          <Grid container spacing={2}>
            {map(componentOperations, (componentOperation, componentIndex) => (
              <Grid
                item
                xs={12}
                key={`${componentIndex}-${componentOperation?.componentStainTypeId}-${
                  componentOperation?.displayName
                }-${size(componentOperations)}`}
              >
                <DeconvolutionOperationComponent
                  componentIndex={componentIndex}
                  componentOperation={componentOperation}
                  updatedSettings={updatedSettings}
                  setUpdatedSettings={setUpdatedSettings}
                  stainTypeId={stainTypeId}
                />
              </Grid>
            ))}
          </Grid>
          <Grid container mt={2}>
            <Grid container spacing={2} xs={12} direction="row">
              <Grid item xs={5}>
                <LabelledDropdown
                  label="Select Operation"
                  options={[
                    { text: 'Linear', value: 'linear' },
                    { text: 'Cubic', value: 'cubic' },
                  ]}
                  value={newComponentOperationType}
                  onOptionSelected={(newValue: DeconvolutionOperation['deconvolutionType']) =>
                    setNewComponentOperationType(newValue)
                  }
                />
              </Grid>
              <Grid item xs={5}>
                <LabelledDropdown
                  label="Select Component"
                  options={map(stainComponentsIds, (stainComponentId) => ({
                    text: stainTypeIdToDisplayName(stainComponentId),
                    value: stainComponentId,
                  }))}
                  value={newStainComponent}
                  onOptionSelected={(newValue) => setNewStainComponent(newValue)}
                />
              </Grid>
              <Grid item xs={2}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setUpdatedSettings({
                      ...updatedSettings,
                      deconvolutionSettings: {
                        ...updatedSettings?.deconvolutionSettings,
                        stainToComponentOperation: {
                          ...updatedSettings?.deconvolutionSettings?.stainToComponentOperation,
                          [stainTypeId]: concat(componentOperations, {
                            deconvolutionType: newComponentOperationType,
                            componentStainTypeId: newStainComponent,
                            coefficients: deconvolutionTypeToDefaultCoefficients(newComponentOperationType),
                          } as DeconvolutionOperation),
                        },
                      },
                    });
                  }}
                >
                  Add Operation
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};
